
import React from 'react';
import LazyImage from '../ui/LazyImage';

/**
 * Landing and login page UI.
 * When `user` and `onProceedToApp` are provided, the sign-in section is replaced
 * with a welcome message and a "Proceed to App" call-to-action.
 */
type AuthLandingProps = {
    authError: string;
    onSignIn: () => void;
    onGoogleSignIn?: () => void;
    onSelectPolicy: (page: 'privacy' | 'terms' | 'impressum') => void;
    /** Callback to open one of the top-nav info pages. */
    onSelectInfoPage: (page: 'philosophy' | 'pricing') => void;
    /** Present only when the user is already authenticated. */
    user?: { name: string } | null;
    /** Callback to navigate from the landing page back into the app. */
    onProceedToApp?: () => void;
};

export default function AuthLanding({ authError, onSignIn, onGoogleSignIn, onSelectPolicy, onSelectInfoPage, user, onProceedToApp }: AuthLandingProps) {
    const isSignedIn = Boolean(user && onProceedToApp);

    const infoRows = [
        {
            type: 'tagline',
            text: 'Minimal, interactive, and intelligent product ideation for a complex world.',
        },
        {
            type: 'info',
            heading: 'Feature placeholder 1',
            description: 'Break down complexity with Opportunity Solution Trees.',
            subtext: 'Use a proven framework for product ideation and discovery in a highly interactive and easy to use environment for every member in a product team.',
            subtext2: 'Zeytune is not just another product management or a sticky notes app, but a carefully crafted solution that prioritizes usability and accessibility, with laser focus to support ideation workflows.',
        },
        {
            type: 'info',
            heading: 'Feature placeholder 2',
            description: 'Intelligence and connected information built into your journey of discovery.',
            subtext: 'Tired of adding sticky notes in your existing app and connecting them with arrows? Zeytune gives you a fluid experience where intelligent nodes allow secure storage and access of all your product research, and guided, customized workflows.',
            subtext2: '',
        },
        {
            type: 'info',
            heading: 'Feature placeholder 3',
            description: 'Simple and elegant drag-connect functionality to build your information network.',
            subtext: 'Connect nodes and elements together in a natural way instead of having to struggle with spreadsheets and bloated documents.',
            subtext2: 'Build relationships between adjacent nodes in your trees for greater contextual depth during AI based analyses.',
        },
        {
            type: 'info',
            heading: 'Feature placeholder 4',
            description: 'Collect, analyze, and connect insights from user and market research to initiatives.',
            subtext: 'A powerful way to consolidate and make sense of insights acquired from discovery sessions, that feeds into every relevant and connect branch of your trees.',
            subtext2: '',
        },
        {
            type: 'info',
            heading: 'Feature placeholder 5',
            description: 'AI powered slicing for opportunities, stories, and experiments to get you out of those ideation blocks, fast.',
            subtext: 'No more chatting with bots to make sense of all your documents and data. The information network you build during your discovery feeds into AI based slicing, to give you ideas on breaking down bigger initiatives into smaller, managebale chunks.',
            subtext2: '',
        },
    ];

    const authPanelContent = isSignedIn ? (
        <>
            <p className='landing-welcome-label'>Welcome, {user!.name}</p>
            <button className='landing-proceed-btn' onClick={onProceedToApp}>
                Proceed to App →
            </button>
        </>
    ) : (
        <>
            {authError && <div className='auth-error'>{authError}</div>}
            <button className='linkedin-btn' onClick={onSignIn}>
                <LinkedInIcon />
                Sign in with LinkedIn
            </button>
            <button className='google-btn' onClick={() => onGoogleSignIn && onGoogleSignIn()}>
                <LazyImage src='/google-icon.png' alt='Google' className='google-icon-img' style={{ width: '18px', height: '18px' }} />
                Sign in with Google
            </button>
        </>
    );

    const authPanel = (
        <div className='auth-container-landing auth-container-with-image'>
            <div className='auth-image-wrapper'>
                {/* Decorative background illustration — no spinner needed as it is absolutely positioned and doesn't affect layout */}
                <img src='/olive-tree.png' alt='Olive tree' className='auth-image lazy-image--loading' onLoad={(e) => e.currentTarget.classList.remove('lazy-image--loading')} />
            </div>
            <div className='auth-content-overlay'>{authPanelContent}</div>
        </div>
    );

    return (
        <div className='landing-page'>
            <nav className='landing-topnav'>
                <button className='landing-topnav-btn' onClick={() => onSelectInfoPage('philosophy')}>Philosophy</button>
                <span className='landing-topnav-dot'>·</span>
                <button className='landing-topnav-btn' onClick={() => onSelectInfoPage('pricing')}>Pricing</button>
            </nav>

            <header className='landing-header'>
                <h1 className='landing-title'>Zeytune</h1>
            </header>

            <main className='landing-content'>
                {infoRows.map((row, index) => {
                    if (row.type === 'tagline') {
                        return (
                            <section key='tagline-row' className='landing-row landing-hero-box'>
                                <div className='landing-row-cell landing-row-text landing-row-tagline'>
                                    <p className='landing-tagline-big'>{row.text}</p>
                                </div>
                                <div className='landing-row-cell landing-row-auth'>{authPanel}</div>
                            </section>
                        );
                    }

                    const rowIndex = index - 1; // 0..2 for regular info rows
                    const imageFirst = rowIndex % 2 === 0;

                    const imageSrc =
                        rowIndex === 0
                            ? '/main1.png'
                            : rowIndex === 1
                                ? '/main2.png'
                                : rowIndex === 2
                                    ? '/zeytune-dragconnect-1.gif'
                                    : rowIndex === 3
                                        ? '/zeytune-insights-1.gif'
                                        : rowIndex === 4
                                            ? '/zeytune-slicing-2.gif'
                                            : undefined;

                    const imageCell = (
                        <div className='landing-row-cell landing-row-image' key='image'>
                            {imageSrc ? (
                                <LazyImage
                                    src={imageSrc}
                                    alt={`Row ${rowIndex + 2} illustration`}
                                    className='landing-image'
                                    wrapperStyle={{ display: 'block', width: '100%', height: '100%' }}
                                />
                            ) : (
                                <div className='landing-image-placeholder'>Image placeholder</div>
                            )}
                        </div>
                    );

                    // Text is on the right when image comes first; right-align it in that case
                    const textAlignClass = imageFirst ? 'landing-row-text-right' : '';

                    const textCell = (
                        <div className={`landing-row-cell landing-row-text ${textAlignClass}`} key='text'>
                            <p>{row.description}</p>
                            <p className='landing-row-subtext'>{(row as any).subtext}</p>
                            {(row as any).subtext2 && (
                                <p className='landing-row-subtext landing-row-subtext-secondary'>{(row as any).subtext2}</p>
                            )}
                        </div>
                    );

                    return (
                        <React.Fragment key={`row-${index}`}>
                            {index > 1 && <div className='landing-row-divider' />}
                            {/* landing-row--image-first marks rows where the image leads on desktop,
                                so mobile CSS can reorder text before image when stacked */}
                            <section className={`landing-row${imageFirst ? ' landing-row--image-first' : ''}`}>
                                {imageFirst ? imageCell : textCell}
                                {imageFirst ? textCell : imageCell}
                            </section>
                        </React.Fragment>
                    );
                })}
            </main>

            <div className='landing-policy-footer'>
                <button type='button' onClick={() => onSelectPolicy('privacy')} className='policy-text-btn'>
                    Privacy Policy
                </button>
                <span>·</span>
                <button type='button' onClick={() => onSelectPolicy('terms')} className='policy-text-btn'>
                    Terms of Service
                </button>
                <span>·</span>
                <button type='button' onClick={() => onSelectPolicy('impressum')} className='policy-text-btn'>
                    Impressum
                </button>
            </div>
        </div>
    );
}

function LinkedInIcon() {
    return (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' className='linkedin-icon'>
            <path d='M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z' />
        </svg>
    );
}

// Replaced the inline SVG with an image asset from public/google-icon.png
